import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, Button, Card, Modal, FormField, Input, Select, Textarea, Badge, EditableText } from '../ui';
import { Plus, Play, Youtube, Video, Trash2, Star, Filter } from 'lucide-react';

const INITIAL_VIDEOS = [
  { id: 'v1', title: 'Summer Classic 2025 — U12 Championship', description: 'Thunder FC defeats Storm FC 3-1 in a dominant performance. Goals by Torres, Rivera, and Martinez.', date: '2025-06-08', category: 'Tournament', ageGroup: 'U12', team: 'Thunder FC', url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', thumbnail: '', duration: '12:34', views: 842, featured: true },
  { id: 'v2', title: 'Lightning Squad — Best Goals of Spring', description: 'Top 10 goals from the Lightning Squad U14 spring season compilation.', date: '2025-05-15', category: 'Highlights', ageGroup: 'U14', team: 'Lightning Squad', url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', thumbnail: '', duration: '6:18', views: 1243, featured: true },
  { id: 'v3', title: 'UC Elites Skills Training — Session 8', description: 'Coach Rivera leads advanced dribbling and finishing drills. Great for players of all ages.', date: '2025-04-20', category: 'Training', ageGroup: 'All', team: 'UC Elites', url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', thumbnail: '', duration: '22:05', views: 567, featured: false },
  { id: 'v4', title: 'Eastside United vs River Hawks — Full Match', description: 'Pool play match from the Spring Invitational. End-to-end action for the U10 division.', date: '2025-04-05', category: 'Full Match', ageGroup: 'U10', team: 'Eastside United', url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', thumbnail: '', duration: '45:20', views: 389, featured: false },
  { id: 'v5', title: 'North Stars U16 — Season Preview', description: 'Introducing the 2025 North Stars roster. Meet the coaches and players ahead of the summer season.', date: '2025-03-28', category: 'Team Feature', ageGroup: 'U16', team: 'North Stars', url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', thumbnail: '', duration: '8:44', views: 712, featured: false },
  { id: 'v6', title: 'Blue Wave — Tournament Champions 2024', description: 'Recap of the Blue Wave championship run at the Fall Cup 2024.', date: '2024-10-12', category: 'Tournament', ageGroup: 'U12', team: 'Blue Wave', url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', thumbnail: '', duration: '9:22', views: 1891, featured: false },
];

const CATEGORIES = ['All', 'Highlights', 'Tournament', 'Full Match', 'Training', 'Team Feature'];
const AGE_GROUPS = ['All', 'U8', 'U10', 'U12', 'U14', 'U16', 'U19'];

// YouTube embed URL helper
const toEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^&\n?#]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}` : url;
};

function VideoCard({ video, isAdmin, onDelete, onUpdate, onPlay }) {
  const embedUrl = toEmbedUrl(video.url);
  return (
    <div style={{ background: 'var(--surface-0)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', transition: 'all 0.18s', position: 'relative' }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold-border)'; e.currentTarget.style.boxShadow = 'var(--shadow-gold)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = ''; }}>
      {video.featured && (
        <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 2, background: 'linear-gradient(135deg, var(--gold-deep), var(--gold-bright))', color: '#0a0a0a', fontSize: 9, fontWeight: 800, padding: '3px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 3 }}>
          <Star size={8} /> Featured
        </div>
      )}
      {/* Thumbnail / play area */}
      <div style={{ background: 'linear-gradient(135deg, #0a0a0a, #1a1a1a)', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', overflow: 'hidden' }}
        onClick={() => onPlay(video)}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(230,172,0,0.04), transparent)' }} />
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg, var(--gold-deep), var(--gold-bright))', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(230,172,0,0.5)', transition: 'transform 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
          <Play size={22} fill="#0a0a0a" color="#0a0a0a" style={{ marginLeft: 3 }} />
        </div>
        {/* Duration badge */}
        <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,0.75)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>{video.duration}</div>
        {/* Category */}
        <div style={{ position: 'absolute', bottom: 8, left: 8, background: 'rgba(0,0,0,0.75)', color: 'var(--gold-bright)', fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{video.category}</div>
      </div>

      <div style={{ padding: '12px 14px' }}>
        <EditableText value={video.title} isAdmin={isAdmin} onSave={v => onUpdate(video.id, 'title', v)}
          style={{ fontSize: 14, fontWeight: 800, fontFamily: 'var(--font-condensed)', color: 'var(--text-primary)', display: 'block', marginBottom: 4, lineHeight: 1.3, letterSpacing: '0.02em' }} tag="div" />
        <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>{video.team}</span>
          <span style={{ fontSize: 11, color: 'var(--border-dark)' }}>·</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{video.ageGroup}</span>
          <span style={{ fontSize: 11, color: 'var(--border-dark)' }}>·</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{video.date}</span>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5, marginBottom: 10 }}>{video.description}</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>👁 {video.views?.toLocaleString()} views</span>
          <div style={{ display: 'flex', gap: 6 }}>
            {isAdmin && (
              <button onClick={() => onDelete(video.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e57373', padding: 4, display: 'flex', borderRadius: 4 }}><Trash2 size={13} /></button>
            )}
            <button onClick={() => onPlay(video)} style={{ fontSize: 12, fontWeight: 700, color: 'var(--gold-deep)', background: 'var(--gold-pale)', border: '1px solid var(--gold-border)', padding: '4px 10px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              <Play size={10} fill="currentColor" /> Watch
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Highlights() {
  const { isAdmin } = useAuth();
  const [videos, setVideos] = useState(INITIAL_VIDEOS);
  const [category, setCategory] = useState('All');
  const [ageGroup, setAgeGroup] = useState('All');
  const [showAdd, setShowAdd] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', url: '', date: new Date().toISOString().split('T')[0], category: 'Highlights', ageGroup: 'U12', team: '', duration: '', featured: false });

  const filtered = videos.filter(v => {
    if (category !== 'All' && v.category !== category) return false;
    if (ageGroup !== 'All' && v.ageGroup !== ageGroup && v.ageGroup !== 'All') return false;
    return true;
  });

  const featured = videos.filter(v => v.featured);
  const updateVideo = (id, key, val) => setVideos(prev => prev.map(v => v.id === id ? { ...v, [key]: val } : v));
  const deleteVideo = (id) => setVideos(prev => prev.filter(v => v.id !== id));

  const submitAdd = (e) => {
    e.preventDefault();
    setVideos(prev => [...prev, { ...form, id: 'v' + Date.now(), views: 0 }]);
    setShowAdd(false);
    setForm({ title: '', description: '', url: '', date: new Date().toISOString().split('T')[0], category: 'Highlights', ageGroup: 'U12', team: '', duration: '', featured: false });
  };

  const embedUrl = playing ? toEmbedUrl(playing.url) : null;

  return (
    <div>
      <PageHeader title="Video Highlights" subtitle={`${videos.length} videos · games, training, and team features`}
        actions={isAdmin && <Button variant="primary" onClick={() => setShowAdd(true)}><Plus size={14} /> Add video</Button>}
      />

      {/* Featured reel */}
      {featured.length > 0 && category === 'All' && ageGroup === 'All' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--gold-muted)', textTransform: 'uppercase', letterSpacing: '0.12em', fontFamily: 'var(--font-condensed)', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Star size={12} fill="currentColor" /> Featured Videos
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
            {featured.map(v => (
              <VideoCard key={v.id} video={v} isAdmin={isAdmin} onDelete={deleteVideo} onUpdate={updateVideo} onPlay={setPlaying} />
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setCategory(cat)}
              style={{ padding: '5px 14px', borderRadius: 20, border: category === cat ? 'none' : '1px solid var(--border-dark)', background: category === cat ? 'linear-gradient(135deg, var(--gold-deep), var(--gold-bright))' : 'var(--surface-0)', color: category === cat ? '#0a0a0a' : 'var(--text-secondary)', fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}>
              {cat}
            </button>
          ))}
        </div>
        <Select style={{ width: 'auto', fontSize: 13 }} value={ageGroup} onChange={e => setAgeGroup(e.target.value)}>
          {AGE_GROUPS.map(ag => <option key={ag}>{ag === 'All' ? 'All ages' : ag}</option>)}
        </Select>
        <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 'auto' }}>{filtered.length} videos</span>
      </div>

      {/* Video grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
        {filtered.map(v => (
          <VideoCard key={v.id} video={v} isAdmin={isAdmin} onDelete={deleteVideo} onUpdate={updateVideo} onPlay={setPlaying} />
        ))}
        {filtered.length === 0 && (
          <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            <Video size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
            <p style={{ fontWeight: 700 }}>No videos match your filters</p>
          </div>
        )}
      </div>

      {/* Video player modal */}
      <Modal open={!!playing} onClose={() => setPlaying(null)} title={playing?.title || 'Video'} width={720}>
        {playing && (
          <div>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 10, overflow: 'hidden', marginBottom: '1rem', background: '#000' }}>
              <iframe
                src={embedUrl}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={playing.title}
              />
            </div>
            <h3 style={{ fontFamily: 'var(--font-condensed)', fontWeight: 800, fontSize: 17, color: 'var(--text-primary)', letterSpacing: '0.03em', marginBottom: 6 }}>{playing.title}</h3>
            <div style={{ display: 'flex', gap: 10, marginBottom: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ background: 'var(--gold-pale)', color: 'var(--gold-muted)', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, border: '1px solid var(--gold-border)' }}>{playing.category}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{playing.team} · {playing.ageGroup} · {playing.date}</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 'auto' }}>👁 {playing.views?.toLocaleString()}</span>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{playing.description}</p>
          </div>
        )}
      </Modal>

      {/* Add video modal */}
      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add video" width={520}>
        <form onSubmit={submitAdd} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <FormField label="Video title" required><Input value={form.title} onChange={e => setForm(p=>({...p,title:e.target.value}))} required placeholder="e.g. Summer Classic Highlights" /></FormField>
          <FormField label="YouTube / video URL" required hint="Paste a YouTube URL">
            <Input value={form.url} onChange={e => setForm(p=>({...p,url:e.target.value}))} required placeholder="https://youtube.com/watch?v=..." />
          </FormField>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Category">
              <Select value={form.category} onChange={e => setForm(p=>({...p,category:e.target.value}))}>
                {['Highlights','Tournament','Full Match','Training','Team Feature'].map(c=><option key={c}>{c}</option>)}
              </Select>
            </FormField>
            <FormField label="Age group">
              <Select value={form.ageGroup} onChange={e => setForm(p=>({...p,ageGroup:e.target.value}))}>
                {['All','U8','U10','U12','U14','U16','U19'].map(ag=><option key={ag}>{ag}</option>)}
              </Select>
            </FormField>
            <FormField label="Team"><Input value={form.team} onChange={e => setForm(p=>({...p,team:e.target.value}))} placeholder="Team name" /></FormField>
            <FormField label="Duration"><Input value={form.duration} onChange={e => setForm(p=>({...p,duration:e.target.value}))} placeholder="12:34" /></FormField>
            <FormField label="Date"><Input type="date" value={form.date} onChange={e => setForm(p=>({...p,date:e.target.value}))} /></FormField>
            <FormField label=" ">
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, paddingTop: 8 }}>
                <input type="checkbox" checked={form.featured} onChange={e => setForm(p=>({...p,featured:e.target.checked}))} />
                <span style={{ fontWeight: 600 }}>Mark as featured</span>
              </label>
            </FormField>
          </div>
          <FormField label="Description"><Textarea value={form.description} onChange={e => setForm(p=>({...p,description:e.target.value}))} rows={2} placeholder="Brief description of this video…" /></FormField>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button type="button" variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Add video</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
